import React, { useMemo, useRef, useState } from "react";
import {
  Check,
  DollarSign,
  LogOut,
  AlertCircle,
  AlertTriangle,
  Download,
  Upload,
  FileSpreadsheet,
  Loader,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { ACTION_TYPES } from "../../Constant/EmployeeActions.config";
import { fmt, translateBulkMessage } from "../../Constant/EmployeeActions.bulk.i18n";
import { STATUS_COLORS, OUTCOME_COLORS } from "./bulkActions.styles";

const ACTION_ICONS = { salary_change: DollarSign, resignation: LogOut, terminate: AlertCircle };
const actionConfig = (type) => ACTION_TYPES.find((a) => a.id === type) || {};

const useFormatters = (language) =>
  useMemo(() => {
    // Latin digits in both languages (matches the rest of the Changes page).
    const locale = language === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
    const whole = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const cents = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const df = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" });
    return {
      num: (v) => {
        if (v === null || v === undefined || Number.isNaN(v)) return "—";
        const r = Math.round(v * 100) / 100;
        return Number.isInteger(r) ? whole.format(r) : cents.format(r);
      },
      arrow: language === "ar" ? "←" : "→",
      date: (ymd) => {
        if (!ymd) return "—";
        const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
        return df.format(new Date(y, m - 1, d));
      },
    };
  }, [language]);

const Btn = ({ s, variant = "secondary", disabled, busy, children, icon: Icon, ...rest }) => {
  const base = { primary: s.btnPrimary, secondary: s.btnSecondary, tertiary: s.btnTertiary, danger: s.btnDanger }[variant];
  return (
    <button
      type="button"
      className="nxb-btn"
      disabled={disabled || busy}
      style={{ ...base, ...(disabled || busy ? s.btnDisabled : null) }}
      {...rest}
    >
      {busy ? <Loader size={16} className="nxb-spin" /> : Icon ? <Icon size={16} /> : null}
      {children}
    </button>
  );
};
export { Btn };

// ─── Step 1 · Choose ──────────────────────────────────────────────────────────

export function StepChoose({ s, bt, isRTL, meta, metaError, loadMeta, selectedTypes, toggleType, templateLang, setTemplateLang, templateBusy, downloadTemplate, onSkipToUpload }) {
  if (metaError) {
    return (
      <div style={s.alert("error")}>
        <AlertCircle size={18} style={{ flexShrink: 0, color: "#ef4444" }} />
        <div style={{ flex: 1 }}>{metaError}</div>
        <Btn s={s} variant="secondary" icon={RefreshCw} onClick={loadMeta}>{bt.retry}</Btn>
      </div>
    );
  }
  if (!meta) {
    return (
      <div style={{ ...s.progressWrap, color: s.k.muted }}>
        <Loader size={28} className="nxb-spin" />
        <p>{bt.loading}</p>
      </div>
    );
  }
  const Arrow = isRTL ? ChevronLeft : ChevronRight;
  return (
    <>
      <h3 style={s.sectionTitle}>{bt.chooseTitle}</h3>
      <p style={s.sectionHint}>{meta.allowedTypes.length > 1 ? bt.chooseHint : bt.chooseHintSingle}</p>

      <div style={s.cardGrid} role="group" aria-label={bt.chooseTitle}>
        {/* Only the action types this admin level may perform are offered —
            a blocked type isn't shown at all (the server enforces the same). */}
        {["salary_change", "resignation", "terminate"].filter((type) => meta.allowedTypes.includes(type)).map((type) => {
          const cfg = actionConfig(type);
          const Icon = ACTION_ICONS[type];
          const allowed = true;
          const selected = selectedTypes.includes(type);
          return (
            <button
              key={type}
              type="button"
              className="nxb-card"
              role="checkbox"
              aria-checked={selected}
              aria-disabled={!allowed}
              disabled={!allowed}
              title={allowed ? undefined : bt.notAllowed}
              style={s.actionCard(selected, !allowed, cfg.color)}
              onClick={() => allowed && toggleType(type)}
            >
              <span style={s.check(selected)}>{selected && <Check size={15} strokeWidth={3} />}</span>
              <div style={s.actionIcon(cfg.color)}><Icon size={22} /></div>
              <h4 style={s.cardTitle}>{bt.actions[type].title}</h4>
              <p style={s.cardDesc}>{allowed ? bt.actions[type].desc : bt.notAllowed}</p>
            </button>
          );
        })}
      </div>

      <div style={s.templatePanel}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
          <div style={s.fileIcon}><FileSpreadsheet size={22} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{bt.downloadTemplate}</div>
            <div style={{ fontSize: 13, color: s.k.muted, marginTop: 2 }}>{bt.templateTip}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={s.segmented} role="radiogroup" aria-label={bt.templateLanguage}>
            {[["en", "English"], ["ar", "العربية"]].map(([code, label]) => (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={templateLang === code}
                className="nxb-seg"
                style={s.segment(templateLang === code)}
                onClick={() => setTemplateLang(code)}
              >
                {label}
              </button>
            ))}
          </div>
          <Btn s={s} variant="secondary" icon={Download} busy={templateBusy} disabled={!selectedTypes.length} onClick={downloadTemplate}>
            {templateBusy ? bt.downloadingTemplate : bt.downloadTemplate}
          </Btn>
        </div>
      </div>

      <div style={{ marginTop: 14, textAlign: isRTL ? "left" : "right" }}>
        <Btn s={s} variant="tertiary" onClick={onSkipToUpload} disabled={!selectedTypes.length}>
          {bt.haveFile} <Arrow size={16} />
        </Btn>
      </div>
    </>
  );
}

// ─── Step 2 · Upload ──────────────────────────────────────────────────────────

export function StepUpload({ s, bt, meta, file, rows, busy, error, handleFile, removeFile }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = () => inputRef.current?.click();
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };
  const working = busy === "parsing" || busy === "validating";
  return (
    <>
      <h3 style={s.sectionTitle}>{bt.uploadTitle}</h3>
      <p style={s.sectionHint}>{fmt(bt.accepted, { max: meta?.maxRows || 1000 })}</p>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) handleFile(f);
        }}
      />

      {!file || (error && !rows.length) ? (
        <div
          className="nxb-drop"
          role="button"
          tabIndex={0}
          style={s.dropzone(drag)}
          onClick={pick}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pick()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
        >
          <div style={s.dropIcon}>{working ? <Loader size={28} className="nxb-spin" /> : <Upload size={28} />}</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{working ? (busy === "parsing" ? bt.parsing : bt.validating) : bt.dropHere}</div>
          {!working && (
            <>
              <div style={{ color: s.k.muted, fontSize: 13, margin: "8px 0 14px" }}>{bt.or}</div>
              <Btn s={s} variant="primary" icon={FileSpreadsheet} onClick={(e) => { e.stopPropagation(); pick(); }}>{bt.browse}</Btn>
            </>
          )}
        </div>
      ) : (
        <div style={s.fileChip}>
          <div style={s.fileIcon}><FileSpreadsheet size={22} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
            <div style={{ fontSize: 13, color: s.k.muted }}>
              {(file.size / 1024).toFixed(1)} KB · {fmt(bt.rowsFound, { n: rows.length })}
            </div>
          </div>
          {working ? (
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center", color: s.k.muted, fontSize: 13 }}>
              <Loader size={16} className="nxb-spin" /> {bt.validating}
            </span>
          ) : (
            <>
              <Btn s={s} variant="secondary" icon={RefreshCw} onClick={pick}>{bt.replaceFile}</Btn>
              <Btn s={s} variant="tertiary" icon={Trash2} onClick={removeFile}>{bt.removeFile}</Btn>
            </>
          )}
        </div>
      )}

      {error && (
        <div style={s.alert("error")} role="alert">
          <AlertCircle size={18} style={{ flexShrink: 0, color: "#ef4444", marginTop: 2 }} />
          <div>{error}</div>
        </div>
      )}
    </>
  );
}

// ─── Step 3 · Review ──────────────────────────────────────────────────────────

const PAGE = 100;

function SalaryDetail({ s, bt, p, f }) {
  const rowsDef = [
    ["net", bt.net],
    ["gross", bt.gross],
    ["insurance", bt.insurance],
    ["tax", bt.tax],
  ];
  return (
    <div style={s.detailPanel}>
      <table style={s.miniTable}>
        <thead>
          <tr>
            <th style={s.miniTh} />
            <th style={s.miniTh}>{bt.current}</th>
            <th style={s.miniTh}>{bt.new}</th>
            <th style={s.miniTh}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {rowsDef.map(([key, label]) => {
            const o = p.old?.[key];
            const n = p.new?.[key];
            const d = o !== null && n !== null && o !== undefined && n !== undefined ? n - o : null;
            return (
              <tr key={key}>
                <td style={{ ...s.miniTd, fontWeight: 700 }}>{label}</td>
                <td style={s.miniTd}>{f.num(o)}</td>
                <td style={{ ...s.miniTd, fontWeight: 800 }}>{f.num(n)}</td>
                <td style={{ ...s.miniTd, color: d > 0 ? "#10b981" : d < 0 ? "#ef4444" : s.k.muted, fontWeight: 700 }}>
                  {d === null || Math.abs(d) < 0.005 ? "—" : `${d > 0 ? "+" : ""}${f.num(d)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: s.k.muted, marginTop: 10 }}>
        {bt.source[p.source]}
        {p.adjustmentType ? ` · ${bt.adjustmentTypes[p.adjustmentType] || p.adjustmentType}` : ""}
      </div>
    </div>
  );
}

function SeparationDetail({ s, bt, p, f, language }) {
  return (
    <div style={s.detailPanel}>
      <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
        <div><strong>{bt.lastWorkingDay}:</strong> {f.date(p.lastWorkingDay)}</div>
        {p.resignationDate && <div><strong>{bt.resignationDate}:</strong> {f.date(p.resignationDate)}</div>}
        {p.resignationReasonType
          ? <div><strong>{bt.resignationReason}:</strong> {bt.resignationReasons[p.resignationReasonType] || p.resignationReasonType}</div>
          : <div><strong>{bt.terminationType}:</strong> {bt.terminationTypes[p.terminationType] || p.terminationType}</div>}
        {p.eligibleForRehire !== null && p.eligibleForRehire !== undefined && (
          <div><strong>{bt.eligibleForRehire}:</strong> {p.eligibleForRehire ? bt.yes : bt.no}</div>
        )}
        <div>{fmt(bt.stateChange, { from: p.currentState || "Active" })}</div>
        <div style={{ color: s.k.muted }}>{bt.payrollProrated}</div>
      </div>
    </div>
  );
}

export function StepReview({ s, bt, language, isRTL, validation, downloadErrorReport }) {
  const f = useFormatters(language);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [limit, setLimit] = useState(PAGE);
  const totals = validation?.totals || { total: 0, valid: 0, warning: 0, error: 0 };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (validation?.rows || []).filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      return [r.clientCode, r.employee?.name, r.employee?.arabicName, r.employee?.infinityCode]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [validation, filter, query]);

  const rowKey = (r) => r.rowId ?? r.rowNumber;
  const toggle = (rowNumber) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber); else next.add(rowNumber);
      return next;
    });

  const stats = [
    ["all", bt.total, totals.total, s.k.accent],
    ["valid", bt.valid, totals.valid, STATUS_COLORS.valid],
    ["warning", bt.warning, totals.warning, STATUS_COLORS.warning],
    ["error", bt.error, totals.error, STATUS_COLORS.error],
  ];
  const statusLabel = { valid: bt.statusValid, warning: bt.statusWarning, error: bt.statusError };
  const StatusIcon = { valid: CheckCircle, warning: AlertTriangle, error: XCircle };
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  return (
    <>
      <h3 style={s.sectionTitle}>{bt.reviewTitle}</h3>
      <p style={s.sectionHint}>{bt.reviewHint}</p>

      <div style={s.statGrid}>
        {stats.map(([key, label, value, color]) => (
          <button
            key={key}
            type="button"
            className="nxb-stat"
            aria-pressed={filter === key}
            style={s.stat(color, filter === key)}
            onClick={() => { setFilter(key); setLimit(PAGE); }}
          >
            <div style={s.statValue(color)}>{f.num(value)}</div>
            <div style={s.statLabel}>{label}</div>
          </button>
        ))}
      </div>

      <div style={s.toolbar}>
        <div style={{ position: "relative", flex: "1 1 240px" }}>
          <Search size={16} style={{ position: "absolute", top: 12, [isRTL ? "right" : "left"]: 12, color: s.k.muted }} />
          <input
            className="nxb-search"
            style={s.search}
            placeholder={bt.search}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }}
          />
        </div>
        {(totals.error > 0 || totals.warning > 0) && (
          <Btn s={s} variant="secondary" icon={Download} onClick={downloadErrorReport}>{bt.downloadErrors}</Btn>
        )}
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 36 }} className="nxb-hide-sm" />
              <th style={{ ...s.th, width: 64 }} className="nxb-hide-sm">{bt.colRow}</th>
              <th style={s.th}>{bt.colEmployee}</th>
              <th style={s.th}>{bt.colAction}</th>
              <th style={s.th} className="nxb-hide-sm">{bt.colChange}</th>
              <th style={s.th} className="nxb-hide-sm">{bt.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, limit).map((r) => {
              const cfg = actionConfig(r.actionType);
              const Icon = ACTION_ICONS[r.actionType] || AlertCircle;
              const open = expanded.has(rowKey(r));
              const p = r.preview;
              const SIcon = StatusIcon[r.status];
              const name = language === "ar" ? r.employee?.arabicName || r.employee?.name : r.employee?.name;
              return (
                <React.Fragment key={rowKey(r)}>
                  <tr className="nxb-row" style={{ cursor: p ? "pointer" : "default" }} onClick={() => p && toggle(rowKey(r))}>
                    <td style={s.td} className="nxb-hide-sm">
                      {p && (open ? <ChevronDown size={16} color={s.k.muted} /> : <Chevron size={16} color={s.k.muted} />)}
                    </td>
                    <td style={{ ...s.td, color: s.k.muted, fontVariantNumeric: "tabular-nums" }} className="nxb-hide-sm">
                      {r.rowNumber}
                      {r.sheet && <div style={{ fontSize: 11, whiteSpace: "nowrap", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }} title={r.sheet}>{r.sheet}</div>}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 700 }}>{name || <span style={{ color: s.k.muted }}>{bt.notFound}</span>}</div>
                      <div style={{ fontSize: 12, color: s.k.muted }}>
                        {r.clientCode || "—"}{r.employee?.department ? ` · ${r.employee.department}` : ""}
                      </div>
                      <div className="nxb-show-sm" style={{ marginTop: 6 }}>
                        <span style={s.pill(STATUS_COLORS[r.status])}><SIcon size={12} /> {statusLabel[r.status]}</span>
                      </div>
                      {r.messages.map((m, i) => (
                        <div key={i} style={s.msgLine(m.level)}>
                          {m.level === "error" ? <XCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> : <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />}
                          <span>{translateBulkMessage(bt, m)}</span>
                        </div>
                      ))}
                    </td>
                    <td style={s.td}>
                      {r.actionType ? (
                        <span style={s.actionBadge(cfg.bgColor, cfg.borderColor)}>
                          <Icon size={13} /> {bt.types[r.actionType]}
                        </span>
                      ) : (
                        <span style={{ color: s.k.muted }}>{r.actionTypeRaw || "—"}</span>
                      )}
                    </td>
                    <td style={s.td} className="nxb-hide-sm">
                      {p?.kind === "salary" && (
                        <>
                          <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                            {f.num(p.old.net)} {f.arrow} {f.num(p.new.net)}
                            {p.changePct !== null && p.changePct !== undefined && (
                              <span style={{ display: "inline-block", margin: "0 8px", fontSize: 12, color: p.changePct > 0 ? "#10b981" : p.changePct < 0 ? "#ef4444" : s.k.muted }}>
                                {p.changePct > 0 ? "+" : ""}{f.num(p.changePct)}%
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: s.k.muted }}>
                            {bt.gross} {f.num(p.new.gross)} · {bt.insurance} {f.num(p.new.insurance)}
                          </div>
                        </>
                      )}
                      {p?.kind === "separation" && (
                        <>
                          <div style={{ fontWeight: 700 }}>{f.date(p.lastWorkingDay)}</div>
                          <div style={{ fontSize: 12, color: s.k.muted }}>
                            {p.resignationReasonType
                              ? bt.resignationReasons[p.resignationReasonType] || p.resignationReasonType
                              : r.actionType === "resignation"
                                ? bt.types.resignation
                                : bt.terminationTypes[p.terminationType] || p.terminationType}
                          </div>
                        </>
                      )}
                      {p && (
                        <div style={{ marginTop: 6 }}>
                          <span style={s.pill(p.appliesNow ? "#10b981" : "#3b82f6")}>
                            <Clock size={11} /> {p.appliesNow ? bt.appliesNow : fmt(bt.scheduledFor, { date: f.date(p.effectiveDate || p.lastWorkingDay) })}
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={s.td} className="nxb-hide-sm">
                      <span style={s.pill(STATUS_COLORS[r.status])}>
                        <SIcon size={12} /> {statusLabel[r.status]}
                      </span>
                    </td>
                  </tr>
                  {open && p && (
                    <tr>
                      <td style={{ ...s.td, background: "transparent" }} colSpan={6}>
                        {p.kind === "salary"
                          ? <SalaryDetail s={s} bt={bt} p={p} f={f} />
                          : <SeparationDetail s={s} bt={bt} p={p} f={f} language={language} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: "center", color: s.k.muted, padding: 32 }}>{bt.noRows}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.length > limit && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <Btn s={s} variant="tertiary" onClick={() => setLimit((l) => l + PAGE)}>
            {fmt(bt.showMore, { n: Math.min(PAGE, filtered.length - limit) })}
          </Btn>
        </div>
      )}
    </>
  );
}

// ─── Step 4 · Submit / results ─────────────────────────────────────────────────

export function StepSubmit({ s, bt, language, batch, downloadReport }) {
  const f = useFormatters(language);
  if (!batch) return null;
  const total = batch.queuedRows || 0;
  const done = batch.processedCount || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 100;
  const running = batch.status === "processing";
  const by = batch.byOutcome || {};

  if (running) {
    return (
      <div style={s.progressWrap} aria-live="polite">
        <div style={s.ring}><Loader size={72} strokeWidth={1.5} className="nxb-spin" /></div>
        <h3 style={s.sectionTitle}>{bt.submittingTitle}</h3>
        <p style={{ ...s.sectionHint, marginBottom: 22 }}>{bt.submittingHint}</p>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div style={s.bar}><div style={s.barFill(pct)} /></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: s.k.muted, marginTop: 8 }}>
            <span>{fmt(bt.progress, { done: f.num(done), total: f.num(total) })}</span>
            <span style={{ fontWeight: 800, color: s.k.text }}>{pct}%</span>
          </div>
        </div>
      </div>
    );
  }

  const title = batch.status === "completed" ? bt.doneTitle : batch.status === "failed" ? bt.failedTitle : bt.doneWithErrors;
  const headColor = batch.status === "completed" ? "#10b981" : batch.status === "failed" ? "#ef4444" : "#f59e0b";
  const HeadIcon = batch.status === "completed" ? CheckCircle : batch.status === "failed" ? XCircle : AlertTriangle;
  const tiles = ["completed", "scheduled", "pending_approval", "failed", "skipped"].filter((k) => by[k]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div style={{ ...s.actionIcon(headColor), marginBottom: 0 }}><HeadIcon size={22} /></div>
        <div>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>{title}</h3>
          <div style={{ fontSize: 13, color: s.k.muted }}>{batch.fileName}</div>
        </div>
      </div>

      {batch.status === "interrupted" && (
        <div style={{ ...s.alert("warning"), marginTop: 0, marginBottom: 16 }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, color: "#f59e0b", marginTop: 2 }} />
          <div>{bt.interrupted}</div>
        </div>
      )}

      <div style={{ ...s.statGrid }}>
        {tiles.map((key) => (
          <div key={key} style={s.resultTile(OUTCOME_COLORS[key])}>
            <div style={s.statValue(OUTCOME_COLORS[key])}>{f.num(by[key])}</div>
            <div style={s.statLabel}>{bt.outcomes[key]}</div>
          </div>
        ))}
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 64 }}>{bt.colRow}</th>
              <th style={s.th}>{bt.colEmployee}</th>
              <th style={s.th}>{bt.colAction}</th>
              <th style={s.th}>{bt.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {(batch.rows || []).map((r) => {
              const cfg = actionConfig(r.actionType);
              const Icon = ACTION_ICONS[r.actionType] || AlertCircle;
              const color = OUTCOME_COLORS[r.outcome] || s.k.muted;
              const detail = r.outcome === "skipped" && r.messages?.length
                ? r.messages.map((m) => translateBulkMessage(bt, m)).join(" · ")
                : r.outcome === "failed" ? r.message : null;
              return (
                <tr key={r.rowId ?? r.rowNumber} className="nxb-row">
                  <td style={{ ...s.td, color: s.k.muted }}>
                    {r.rowNumber}
                    {r.sheet && <div style={{ fontSize: 11, whiteSpace: "nowrap" }}>{r.sheet}</div>}
                  </td>
                  <td style={s.td}>
                    <div style={{ fontWeight: 700 }}>{r.employeeName || r.clientCode || "—"}</div>
                    <div style={{ fontSize: 12, color: s.k.muted }}>
                      {r.clientCode || ""}{r.actionId ? ` · ${fmt(bt.actionNo, { id: r.actionId })}` : ""}
                    </div>
                    {detail && <div style={s.msgLine("error")}><XCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{detail}</span></div>}
                  </td>
                  <td style={s.td}>
                    {r.actionType ? (
                      <span style={s.actionBadge(cfg.bgColor, cfg.borderColor)}><Icon size={13} /> {bt.types[r.actionType]}</span>
                    ) : "—"}
                  </td>
                  <td style={s.td}><span style={s.pill(color)}>{bt.outcomes[r.outcome] || r.outcome}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16 }}>
        <Btn s={s} variant="secondary" icon={Download} onClick={downloadReport}>{bt.downloadResults}</Btn>
      </div>
    </>
  );
}
